import styleText from './as-option.css?raw';
import { upgradeProperty } from './utils';

const template = document.createElement('template');
template.innerHTML = `
  <div class="wrapper">
    <span class="indicator" aria-hidden="true"></span>
    <span class="content">
      <slot></slot>
    </span>
  </div>
`;

/**
 * as-option custom element for use with as-dropdown (light DOM).
 */
export class AsOption extends HTMLElement {
  static get observedAttributes() {
    return ['value', 'selected', 'disabled', 'label'];
  }

  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  private _shadowRoot?: ShadowRoot;

  constructor() {
    super();
    upgradeProperty(this, 'selected');
    upgradeProperty(this, 'disabled');
    upgradeProperty(this, 'value');
    upgradeProperty(this, 'label');
  }

  connectedCallback() {
    this.tabIndex = -1;
    this.setAttribute('role', 'option');
    if (!this._shadowRoot) {
      this._shadowRoot = this.attachShadow({ mode: 'open' });
      this._shadowRoot.appendChild(template.content.cloneNode(true));
      this._shadowRoot.adoptedStyleSheets = [
        ...this._shadowRoot.adoptedStyleSheets,
        AsOption.sheet,
      ];
    }
    this._render();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null) {
    if (oldValue !== newValue) {
      if (name === 'disabled') {
        this._updateCursor();
      }
      this._render();
    }
  }

  get value() {
    return this.getAttribute('value') ?? this.textContent ?? '';
  }
  set value(val: string) {
    this.setAttribute('value', val);
  }

  get selected() {
    return this.hasAttribute('selected');
  }
  set selected(val: boolean) {
    if (val) {
      this.setAttribute('selected', '');
    } else {
      this.removeAttribute('selected');
    }
  }

  get disabled() {
    return this.hasAttribute('disabled');
  }
  set disabled(val: boolean) {
    if (val) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  get label() {
    return this.getAttribute('label') ?? this.textContent ?? '';
  }
  set label(val: string) {
    this.setAttribute('label', val);
  }

  private _render() {
    this.classList.toggle('selected', this.selected);
    this.classList.toggle('disabled', this.disabled);
    this.setAttribute('aria-selected', this.selected ? 'true' : 'false');
    this.setAttribute('aria-disabled', this.disabled ? 'true' : 'false');
    this._updateCursor();
  }

  private _updateCursor() {
    this.style.cursor = this.disabled ? 'not-allowed' : 'pointer';
  }
}

customElements.define('as-option', AsOption);
